/** Also runs for untyped contract-generation inputs: exposure is never inferred. */
export function defineOperation(operation) {
    if (!["public", "app", "worker", "operator"].includes(operation.audience)) {
        throw new Error(`Operation ${operation.operationId} requires an explicit audience.`);
    }
    if (operation.audience === "public" && (!operation.requiredScope || !["read", "create"].includes(operation.rateClass))) {
        throw new Error(`Public operation ${operation.operationId} requires scope and rate metadata.`);
    }
    if (operation.response.kind === "json" && ![200, 201].includes(operation.response.status)) {
        throw new Error(`Operation ${operation.operationId} requires a supported JSON success status.`);
    }
    return operation;
}
function validSegment(value) {
    if (!value || value === "." || value === ".." || /[\u0000-\u001f\u007f]/u.test(value)) {
        throw new URIError("Invalid operation path parameter.");
    }
    return value;
}
/** Parameters occupy whole segments. Match and build decode/encode exactly once. */
export function compilePathTemplate(template) {
    const segments = template.split("/");
    const keys = new Map();
    if (!template.startsWith("/") || /[?#%\\]/u.test(template))
        throw new Error(`Invalid path template: ${template}`);
    const patterns = segments.map((segment, index) => {
        if (index === 0)
            return "";
        const parameter = /^\{([a-zA-Z][a-zA-Z0-9_]*)\}$/u.exec(segment)?.[1];
        if (parameter) {
            if ([...keys.values()].includes(parameter))
                throw new Error(`Duplicate path parameter: ${parameter}`);
            keys.set(index, parameter);
            return "[^/]+";
        }
        if (!segment || /[{}]/u.test(segment) || segment === "." || segment === "..") {
            throw new Error(`Invalid path template: ${template}`);
        }
        return segment.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
    });
    const pattern = new RegExp(`^${patterns.join("/")}$`, "u");
    return {
        pattern,
        match(pathname) {
            if (!pattern.test(pathname))
                return null;
            const values = pathname.split("/");
            return Object.fromEntries([...keys].map(([index, key]) => [key, validSegment(decodeURIComponent(values[index]))]));
        },
        build(params = {}) {
            if (Object.keys(params).length !== keys.size || [...keys.values()].some((key) => typeof params[key] !== "string")) {
                throw new Error(`Path parameters do not match ${template}.`);
            }
            return segments.map((segment, index) => {
                const key = keys.get(index);
                return key === undefined ? segment : encodeURIComponent(validSegment(params[key]));
            }).join("/");
        },
    };
}
/** A consumer must name its surface before obtaining a route or contract metadata. */
export function compileOperation(operation, audience) {
    defineOperation(operation);
    if (operation.audience !== audience) {
        throw new Error(`Operation ${operation.operationId} has audience ${operation.audience}, not ${audience}.`);
    }
    const route = compilePathTemplate(operation.path);
    return { ...operation, ...route, match(pathname) {
            const params = route.match(pathname);
            if (params && operation.request.params) {
                const parsed = operation.request.params.safeParse(params);
                if (!parsed.success)
                    throw new URIError("Invalid operation path parameters.");
                // Normalization must not produce a segment that the request builder refuses.
                for (const value of Object.values(parsed.data))
                    validSegment(value);
                return parsed.data;
            }
            return params;
        } };
}
/** Static SDK coverage comes from the same declarations as the routes. */
export function operationSdkMethods(operations, audience) {
    const ids = new Set();
    return Object.fromEntries(operations.map((operation) => {
        const compiled = compileOperation(operation, audience);
        if (ids.has(compiled.operationId))
            throw new Error(`Duplicate SDK operation: ${compiled.operationId}`);
        ids.add(compiled.operationId);
        if (!compiled.sdk)
            throw new Error(`Operation ${compiled.operationId} has no SDK mapping.`);
        return [compiled.operationId, [compiled.sdk.namespace, compiled.sdk.method]];
    }));
}
